import * as React from "react";

import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";
import { TextField, Fabric, Theme, mergeStyleSets, Calendar, IDatePickerStrings, DayOfWeek } from "@nuorder/components";
import startOfDay from "date-fns/startOfDay";
import { IDate, isValidDateFormat, generateDateValueObject } from "./editor-helper";
import { getDatePickerString } from "helpers/dates";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IProps {
    [key: string]: any;
    defaultDateFormat?: string;
}

interface IState {
    value: IDate;
    editMode: EDIT_MODES;
    inputString: string;
    initDate: Date;
    fromCalendar: boolean;
    remove: boolean;
}

enum EDIT_MODES {
    REPLACE,
    APPEND,
}

export class DatePickerCellEditor extends React.PureComponent<IProps, IState> {
    private inputRef;
    private takeDefaultValue = false;
    private defaultValue = undefined;
    private classes = mergeStyleSets({
        root: {
            input: {
                width: "100%",
                height: "125px",
            },
        },
    });
    constructor(props) {
        super(props);
        let editMode = EDIT_MODES.APPEND;
        let value = props.value;
        let remove = false;
        const fromCalendar = false;
        this.defaultValue = generateDateValueObject(value, undefined, [props.defaultDateFormat], true);
        let inputString;
        let initDate;
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            value = undefined;
            remove = true;
        } else if (props.charPress) {
            value = this.defaultValue;
            inputString = props.charPress;
            editMode = EDIT_MODES.REPLACE;
        } else {
            value = this.defaultValue;
        }

        if (
            this.defaultValue?.year !== undefined &&
            this.defaultValue?.month !== undefined &&
            this.defaultValue?.day !== undefined
        ) {
            const dateString = `${this.defaultValue?.month}/${this.defaultValue?.day}/${this.defaultValue?.year}`;
            initDate = new Date(dateString);
        } else {
            initDate = new Date();
        }
        this.state = {
            value,
            editMode,
            inputString,
            initDate,
            fromCalendar,
            remove,
        };
        this.inputRef = React.createRef();
    }

    componentDidMount() {
        this.focus();
    }

    // Check if cell should select all when starts editing
    selectAllOnStart() {
        const { charPress, eventKey } = this.props;
        if (eventKey === eventKey.ENTER) {
            // On ENTER press
            return true;
        } else if (!eventKey && !charPress) {
            // On mouse double click or focused on tab
            return true;
        }

        return false;
    }

    focus() {
        if (this.inputRef.current) {
            (this.inputRef.current as any).focus();
        }
    }

    isPopup = (): boolean => {
        return true;
    };

    getValue() {
        if (this.state.remove) {
            return undefined;
        }
        const inputObjectValue = generateDateValueObject(
            this.state.inputString,
            this.defaultValue,
            [this.props.defaultDateFormat],
            false
        );
        const value = this.takeDefaultValue
            ? this.defaultValue
            : this.state.fromCalendar
              ? this.state.value
              : inputObjectValue;
        return value ? { year: value.year, month: value.month, day: value.day } : undefined;
    }

    onSelectDate = (date: Date | null | undefined): void => {
        if (date !== null && date !== undefined) {
            const inputDate = startOfDay(date);

            this.setState({
                value: {
                    year: inputDate.getFullYear(),
                    month: inputDate.getMonth() + 1,
                    day: inputDate.getDate(),
                },
                fromCalendar: true,
            });
        }
        // NOTE: close editor after updated.
        setTimeout(() => {
            this.props.stopEditing(true);
        }, 0);
    };

    bindRef(ref) {
        this.inputRef.current = ref;
    }
    onTextChange(_e, newValue: string) {
        this.setState({
            inputString: newValue,
        });
    }
    handleGetErrorMessage(value: string) {
        return !isValidDateFormat(value, [this.props.defaultDateFormat], false) && value.length > 0
            ? formatMessage("Invalid format")
            : "";
    }
    render() {
        const dayPickerStrings: IDatePickerStrings = getDatePickerString();
        return (
            <div
                className={this.classes.root}
                data-test={DATATEST.ASSORTMENTS.GRID.DATE_PICKER_CELL_EDITOR}
                onKeyDown={this.handleKeyDown}
            >
                <Theme>
                    <Fabric>
                        <TextField
                            placeholder={this.props.defaultDateFormat || formatMessage("MM/DD/YYYY")}
                            componentRef={this.bindRef.bind(this)}
                            onChange={this.onTextChange.bind(this)}
                            iconProps={{ iconName: "Calendar" }}
                            onGetErrorMessage={this.handleGetErrorMessage.bind(this)}
                            autoComplete={"off"}
                            value={this.state.inputString}
                        />
                        <Calendar
                            onSelectDate={this.onSelectDate}
                            isMonthPickerVisible={false}
                            showGoToToday={false}
                            firstDayOfWeek={DayOfWeek.Sunday}
                            strings={dayPickerStrings}
                            today={this.state.initDate}
                        />
                    </Fabric>
                </Theme>
            </div>
        );
    }

    private handleKeyDown = (e: React.KeyboardEvent) => {
        const { editMode } = this.state;
        const { api } = this.props;
        const currentCell = api.getEditingCells()[0];
        switch (e.key) {
            case KEYBOARD_KEYS.LEFT:
            case KEYBOARD_KEYS.RIGHT:
                // Suggestions will cause defaultPrevented to be true if using key events
                if (!e.defaultPrevented && editMode === EDIT_MODES.REPLACE) {
                    this.props.stopEditing();
                    this.navigateToCell(e, currentCell);
                    e.preventDefault();
                }
                break;
            case KEYBOARD_KEYS.ENTER:
                this.props.stopEditing();
                this.navigateToCell(e, currentCell);
                break;
            case KEYBOARD_KEYS.ESCAPE:
                this.takeDefaultValue = true;
                this.props.stopEditing();
                break;
            default:
        }
    };

    private navigateToCell = (e, currentCell) => {
        const { api } = this.props;
        if (currentCell) {
            api.setFocusedCell(currentCell.rowIndex, currentCell.column.getColId());
        }

        const lastRow = api.getLastDisplayedRow();

        const focusedCell = api.getFocusedCell();
        api.clearRangeSelection();
        switch (e.keyCode) {
            case KEYBOARD_KEYS.RIGHT:
                api.tabToNextCell();
                break;
            case KEYBOARD_KEYS.LEFT:
                api.tabToPreviousCell();
                break;
            case KEYBOARD_KEYS.UP:
                if (focusedCell.rowIndex > 0) {
                    api.setFocusedCell(Math.max(0, focusedCell.rowIndex - 1), focusedCell.column.getColId());
                }
                break;
            case KEYBOARD_KEYS.DOWN:
            case KEYBOARD_KEYS.ENTER:
                // covering "new line"
                if (e.ctrlKey) {
                    api.setFocusedCell(
                        Math.min(focusedCell.rowIndex + 1, lastRow),
                        focusedCell.column.getColId(),
                        null
                    );
                }
                break;
            default:
        }
    };
}
